// Login/registro del onboarding de Tiendanube — CLON del `/auth` de Atlas: en desktop es el SPLIT
// (carrusel a la derecha + panel de login a la izquierda), reusando los MISMOS componentes que la web
// real (OnboardingCarousel + AuthSheet). El único agregado es el botón "Continuar con tu Tiendanube".
// Aislado: NO toca AuthForm ni el /auth real; sólo reusa piezas de presentación.
//
// Diferencias de lógica con el AuthForm real (a propósito):
//   - Registro por la edge `tn-onboarding-signup` (cuenta YA confirmada) + `createCompanyForUser` con
//     rubro placeholder 'Otros' (la IA lo ajusta al conectar). SIN selector de rubro.
//   - Tras autenticar → `/tiendanube/conectar` (conectar la tienda), no al home.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Store, Mail, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { MARCA_POR_DEFECTO } from '../../lib/altaDesdeCuestionario';
import { paisDelBrowser } from '../../lib/paisDelBrowser';
import { createCompanyForUser } from '../../lib/createCompany';
import { OnboardingCarousel } from '../../components/onboarding/OnboardingCarousel';
import { getCarouselSlides } from '../../components/onboarding/onboardingSlides';
import { AuthSheet } from '../../components/onboarding/AuthSheet';
import { useIsDesktop } from '../../components/onboarding/useIsDesktop';
import {
  Field,
  PasswordField,
  GoogleButton,
  SubmitButton,
  OrDivider,
  Prompt,
  Feedback,
} from './authPrimitives';

type Mode = 'signup' | 'login' | 'forgot';

// Tras autenticar: conectar la tienda (trae productos y datos). Los 3 caminos convergen ahí.
const NEXT = '/tiendanube/conectar';
const TERMS_URL = 'https://estudio-atlas.com/politica-de-privacidad';
const TITLE_ID = 'tn-auth-title';

const TITLES: Record<Mode, string> = {
  signup: 'Creá tu cuenta',
  login: 'Ingresá a tu cuenta',
  forgot: 'Restablecer contraseña',
};

export default function TiendanubeAuth() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [mode, setMode] = useState<Mode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [continuarLoading, setContinuarLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resetCooldown, setResetCooldown] = useState(0);
  // Registro con email colapsado detrás de un botón (pedido del jefe): se abre al tocar.
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    setError(null);
    setMessage(null);
    setEmailOpen(false); // al cambiar de modo, el registro con email vuelve a colapsarse
  }, [mode]);

  useEffect(() => {
    if (!resetCooldown) return;
    const id = setInterval(() => setResetCooldown((p) => (p <= 1 ? 0 : p - 1)), 1000);
    return () => clearInterval(id);
  }, [resetCooldown]);

  const switchMode = (next: Mode) => setMode(next);

  // "Continuar con tu Tiendanube": login/alta SIN registro manual (OAuth de la tienda). Al volver,
  // /tiendanube/continuar crea/vincula la cuenta sola y entra. Es el diferencial.
  const handleContinuar = async () => {
    setError(null);
    setContinuarLoading(true);
    try {
      const { data, error: err } = await supabase.functions.invoke('tn-continue-start', {});
      const url = (data as { authorizeUrl?: string } | null)?.authorizeUrl;
      if (err || !url) throw new Error('No se pudo iniciar. Probá de nuevo en un momento.');
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo continuar con tu Tiendanube.');
      setContinuarLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}${NEXT}` },
      });
      if (err) throw err;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al continuar con Google');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const mail = email.trim().toLowerCase();
    if (!mail) {
      setError('Ingresá tu correo electrónico');
      return;
    }
    if (mode !== 'forgot' && !password) {
      setError('Ingresá tu contraseña');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const { error: err } = await supabase.auth.signInWithPassword({ email: mail, password });
        if (err) {
          if (err.message === 'Invalid login credentials') {
            throw new Error('Correo electrónico o contraseña incorrectos');
          }
          if (err.message?.includes('Email not confirmed')) {
            throw new Error('Tu email todavía no está confirmado. Revisá tu bandeja de entrada.');
          }
          throw err;
        }
        navigate(NEXT, { replace: true });
        return;
      }

      if (mode === 'forgot') {
        if (resetCooldown > 0) {
          setError(`Esperá ${resetCooldown}s antes de pedir otro email.`);
          setLoading(false);
          return;
        }
        await supabase.auth.resetPasswordForEmail(mail, {
          redirectTo: `${window.location.origin}/restablecer-clave`,
        });
        setResetCooldown(60);
        setMessage(
          'Si existe una cuenta con este email, vas a recibir instrucciones para restablecer tu contraseña.',
        );
        return;
      }

      // Registro: cuenta creada YA confirmada del lado del servidor (sin cortar con el mail).
      const { error: err } = await supabase.functions.invoke('tn-onboarding-signup', {
        body: { email: mail, password, full_name: name.trim() || undefined },
      });
      if (err) {
        let code: string | undefined;
        let serverMsg: string | undefined;
        try {
          const body = await (err as { context?: Response }).context?.json();
          code = body?.code;
          serverMsg = body?.error;
        } catch {
          /* sin cuerpo legible */
        }
        if (code === 'email_exists') {
          setMode('login');
          setMessage('Ya tenés una cuenta con este email. Iniciá sesión para vincular tu tienda.');
          return;
        }
        throw new Error(serverMsg || err.message || 'No se pudo crear la cuenta');
      }

      const { data: signData, error: signErr } = await supabase.auth.signInWithPassword({
        email: mail,
        password,
      });
      if (signErr) throw signErr;

      // Empresa con rubro placeholder 'Otros' (la IA lo ajusta al conectar la tienda). Idempotente.
      const userId = signData.user?.id;
      if (userId) {
        const res = await createCompanyForUser({
          userId,
          name: name.trim() || mail.split('@')[0],
          pais: paisDelBrowser() ?? 'Otro',
          companyName: MARCA_POR_DEFECTO,
          industry: 'Otros',
        });
        if (!res.ok) {
          throw new Error(res.error || 'No se pudo crear tu empresa. Probá de nuevo en un momento.');
        }
      }

      navigate(NEXT, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error inesperado');
    } finally {
      setLoading(false);
    }
  };

  // Contenido del formulario. `variant`: 'page' = mobile a pantalla completa; 'sheet' = panel
  // izquierdo del split de desktop (dentro de AuthSheet). Mismos tamaños/paddings que el AuthForm real.
  const formInner = (variant: 'sheet' | 'page'): ReactNode => {
    const isPage = variant === 'page';
    return (
      <div
        key={mode}
        className={`flex w-full animate-fade-in flex-col gap-6 ${
          isPage ? 'mx-auto max-w-[440px] px-5 pb-10 pt-10' : 'p-5 lg:mx-auto lg:my-auto lg:max-w-[440px] lg:px-6 lg:py-16'
        }`}
      >
        <div className="flex flex-col items-center gap-3">
          <img
            src="/images/estudio-atlas-logo-solo.svg"
            alt="Estudio Atlas"
            draggable={false}
            className={`w-auto select-none ${isPage ? 'h-12' : 'h-11 lg:h-14'}`}
          />
          <h1
            id={TITLE_ID}
            className={`text-center font-[600] text-[#18181B] ${
              isPage ? 'text-[24px] leading-[30px]' : 'text-[20px] leading-[25px] lg:text-[28px] lg:leading-[34px]'
            }`}
          >
            {TITLES[mode]}
          </h1>
        </div>

        <Feedback error={error} message={message} />

        {mode !== 'forgot' && (
          <>
            <button
              type="button"
              onClick={handleContinuar}
              disabled={continuarLoading}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#1644EB] px-4 py-4 text-[15px] font-[700] leading-[20px] text-white transition-colors hover:bg-[#1136c4] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {continuarLoading ? (
                <Loader2 className="size-[18px] animate-spin" />
              ) : (
                <Store className="size-[18px]" />
              )}
              Continuar con tu Tiendanube
            </button>
            <OrDivider />

            <GoogleButton
              label={mode === 'signup' ? 'Registrarme con Google' : 'Iniciar sesión con Google'}
              loading={googleLoading}
              onClick={handleGoogle}
            />
            <OrDivider />
          </>
        )}

        {mode === 'signup' && (
          <>
            {!emailOpen ? (
              // Colapsado: un botón que despliega el form (pedido del jefe — que el
              // registro con email no ocupe tanto y se abra al tocar, tipo dropdown).
              <button
                type="button"
                onClick={() => setEmailOpen(true)}
                className="flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#F4F4F5] px-4 py-4 text-[16px] font-[700] leading-[21px] text-[#18181B] transition-colors hover:bg-[#E4E4E7]"
              >
                <Mail className="size-[18px] text-[#71717B]" />
                Registrarme con email
                <ChevronDown className="size-[18px] text-[#71717B]" />
              </button>
            ) : (
              <form onSubmit={handleSubmit} className="flex w-full animate-fade-in flex-col gap-6" noValidate>
                <div className="flex w-full flex-col gap-4">
                  <Field id="tn-email" label="Correo electrónico" type="email" autoComplete="email" placeholder="tu@correo.com" value={email} onChange={setEmail} />
                  <PasswordField id="tn-password" value={password} onChange={setPassword} show={showPassword} onToggle={() => setShowPassword((s) => !s)} autoComplete="new-password" />
                  <Field id="tn-name" label="Nombre y apellido" autoComplete="name" placeholder="Tu nombre" value={name} onChange={setName} />
                </div>
                <SubmitButton label="Crear cuenta" loading={loading} />
              </form>
            )}
            <Prompt text="¿Ya tenés una cuenta?" actionLabel="Iniciar sesión" onAction={() => switchMode('login')} />
            <span className="h-px w-full bg-[#E4E4E7]" />
            <p className="text-center text-[12px] font-[500] leading-[17px] text-[#18181B]">
              Al continuar, estás aceptando nuestros{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-[700] text-[#3B82F6] hover:underline">
                términos y condiciones
              </a>
            </p>
          </>
        )}

        {mode === 'login' && (
          <>
            <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
              <div className="flex w-full flex-col gap-4">
                <Field id="tn-email" label="Correo electrónico" type="email" autoComplete="email" placeholder="tu@correo.com" value={email} onChange={setEmail} />
                <div className="flex w-full flex-col gap-1.5">
                  <PasswordField id="tn-password" value={password} onChange={setPassword} show={showPassword} onToggle={() => setShowPassword((s) => !s)} autoComplete="current-password" />
                  <button type="button" onClick={() => switchMode('forgot')} className="self-start text-[12px] font-[700] leading-[17px] text-[#3B82F6] hover:underline">
                    Olvidé mi contraseña
                  </button>
                </div>
              </div>
              <SubmitButton label="Iniciar sesión" loading={loading} />
            </form>
            <Prompt text="¿Todavía no tenés una cuenta?" actionLabel="Registrarme" onAction={() => switchMode('signup')} />
          </>
        )}

        {mode === 'forgot' && (
          <>
            <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
              <Field id="tn-email" label="Correo electrónico" type="email" autoComplete="email" placeholder="tu@correo.com" value={email} onChange={setEmail} />
              <SubmitButton label="Enviar" loading={loading} />
            </form>
            <Prompt text="¿Te acordaste de tu contraseña?" actionLabel="Iniciar sesión" onAction={() => switchMode('login')} />
          </>
        )}
      </div>
    );
  };

  // Mobile: pantalla completa (una columna).
  if (!isDesktop) {
    return <div className="min-h-[100dvh] w-full bg-white">{formInner('page')}</div>;
  }

  // Desktop: split idéntico al /auth de Atlas — carrusel de fondo + panel de login a la izquierda.
  return (
    <>
      <OnboardingCarousel slides={getCarouselSlides()} onStart={() => {}} paused={false} />
      <AuthSheet open onClose={() => {}} titleId={TITLE_ID}>
        {formInner('sheet')}
      </AuthSheet>
    </>
  );
}
